<template>
  <!--
    Explicit approval before a campaign that goes beyond the free monthly credits sends. Two modes:
      • WALLET (credit-wallet, 2026-07-10): the practice is out of SMS credits, so sending buys a
        BULK credit top-up (whole reload packs — the same amount auto-reload uses); the campaign
        draws only what it needs and the rest rolls forward. We never micro-charge the card.
      • LEGACY fallback (wallet coverage unavailable / flag off): a plain confirmation that the
        campaign uses credits beyond the free monthly allowance — no per-campaign card charge (the
        old standalone overage Stripe charge was removed 2026-07-09; the wallet supersedes it).
    Either way the customer must tick the checkbox — which names the exact amount — before
    "Approve & Send" enables. Promise-based: call open({...}) and await the boolean. Shared by
    CreateCampaignModal ("Create & Send") and the Campaigns list quick-send so the UX is identical.
  -->
  <b-modal
    v-model="show"
    :title="isWallet ? ('💳 ' + walletCopy.title) : '⚠️ Beyond your free monthly credits'"
    centered
    no-close-on-backdrop
    hide-header-close
    @hide="onHide"
  >
    <template v-if="isWallet">
      <p class="mb-2">{{ walletCopy.lead }}</p>
      <!-- MULTI-POOL: itemize each channel's top-up (SMS: $X, Email: $Y). Single-pool skips this. -->
      <ul v-if="isMulti" class="oam-channels mb-2">
        <li v-for="c in walletCopy.channels" :key="c.product">
          <span class="oam-channels__name">{{ c.label }}</span>
          <span class="oam-channels__amt">${{ c.topUpUsd.toFixed(2) }}</span>
        </li>
      </ul>
      <p class="text-muted mb-3" style="font-size: 0.85rem;">{{ walletCopy.breakdown }}</p>
      <b-form-checkbox v-model="approved" data-testid="overage-approve">
        {{ walletCopy.checkbox }}
      </b-form-checkbox>
    </template>
    <template v-else>
      <p class="mb-2">
        This campaign goes beyond your free monthly message credits. It uses
        <strong>${{ amount.toFixed(2) }}</strong> of SMS credits from your balance.
      </p>
      <p v-if="chargeableSegments > 0" class="text-muted mb-3" style="font-size: 0.85rem;">
        {{ chargeableSegments.toLocaleString() }} message credit{{ chargeableSegments === 1 ? '' : 's' }}
        beyond your free monthly credits × ${{ ratePerSegment.toFixed(2) }} each.
      </p>
      <b-form-checkbox v-model="approved" data-testid="overage-approve">
        I approve using <strong>${{ amount.toFixed(2) }}</strong> of SMS credits.
      </b-form-checkbox>
    </template>
    <template #modal-footer>
      <b-button variant="outline-secondary" @click="resolve(false)">Cancel</b-button>
      <b-button variant="success" :disabled="!approved" @click="resolve(true)">
        Approve &amp; Send
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { buildWalletConsentCopy } from '@/utils/campaignGate';

export default {
  name: 'OverageApprovalModal',
  data() {
    return {
      show: false,
      approved: false,
      amount: 0,
      rawOverage: 0,
      minimumApplied: false,
      chargeableSegments: 0,
      ratePerSegment: 0.02,
      wallet: null,
      // MULTI-POOL (E3): per-channel top-ups + combined total when the server gates more than the
      // SMS pool. Empty / false → single-pool or legacy path (byte-identical to before).
      multiChannel: false,
      channels: [],
      totalTopUpUsd: 0,
      resolveFn: null,
    };
  },
  computed: {
    // Multi-pool consent when the gate carried a walletChannels breakdown.
    isMulti() { return !!this.multiChannel && Array.isArray(this.channels) && this.channels.length > 0; },
    // Wallet mode when the server sent a credit-wallet consent object (single OR multi); else legacy.
    isWallet() { return this.isMulti || !!this.wallet; },
    walletCopy() {
      if (this.isMulti) {
        return buildWalletConsentCopy({ channels: this.channels, totalTopUpUsd: this.totalTopUpUsd });
      }
      return buildWalletConsentCopy(this.wallet);
    },
  },
  methods: {
    // Returns a Promise<boolean> — true if the customer ticked the box and approved.
    // WALLET mode: pass `wallet` (with reloadAmountUsd/appliedUsd/rolloverUsd) — `amount` is the
    // bulk top-up. MULTI-POOL mode: also pass `multiChannel`, `channels`, `totalTopUpUsd` to itemize
    // per-channel top-ups. LEGACY mode: `amount` is the $0.50-floored overage; `rawOverage`+
    // `minimumApplied` disclose a rounded-up small charge.
    open({ amount, rawOverage, minimumApplied, chargeableSegments, ratePerSegment, wallet, multiChannel, channels, totalTopUpUsd } = {}) {
      this.amount = Number(amount) || 0;
      this.rawOverage = Number(rawOverage) || 0;
      this.minimumApplied = !!minimumApplied;
      this.chargeableSegments = Number(chargeableSegments) || 0;
      this.ratePerSegment = Number(ratePerSegment) || 0.02;
      this.wallet = wallet || null;
      this.multiChannel = !!multiChannel;
      this.channels = Array.isArray(channels) ? channels : [];
      this.totalTopUpUsd = Number(totalTopUpUsd) || 0;
      this.approved = false;
      this.show = true;
      return new Promise((res) => { this.resolveFn = res; });
    },
    resolve(ok) {
      const r = this.resolveFn;
      this.resolveFn = null;
      this.show = false;
      if (r) r(!!ok);
    },
    // Backdrop/ESC/×-less close still resolves false so callers never hang.
    onHide() {
      if (this.resolveFn) {
        const r = this.resolveFn;
        this.resolveFn = null;
        r(false);
      }
    },
  },
};
</script>

<style scoped>
/* Multi-pool itemization (SMS: $X, Email: $Y) — a compact per-channel list above the checkbox. */
.oam-channels {
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem;
  border: 1px solid #e9edf1;
  border-radius: 8px;
}
.oam-channels li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.7rem;
  font-size: 0.9rem;
}
.oam-channels li + li {
  border-top: 1px solid #eef1f4;
}
.oam-channels__name {
  font-weight: 600;
  color: #26333d;
}
.oam-channels__amt {
  font-weight: 700;
  color: #26333d;
  font-variant-numeric: tabular-nums;
}
</style>
